import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getOrderGroup } from "@/lib/orders.functions";

export const Route = createFileRoute("/_authenticated/povprasevanje/$groupId")({
  head: () => ({ meta: [{ title: "Skupinsko povpraševanje — kupish.si" }] }),
  component: Page,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-destructive">{(error as Error).message}</div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24">Povpraševanje ne obstaja.</div>
  ),
});

const STATUS_LABEL: Record<string, string> = {
  quote_requested: "Čaka na predračun",
  quote_sent: "Predračun prejet",
  pending: "V obdelavi",
  paid: "Plačano",
  shipped: "Odposlano",
  delivered: "Dostavljeno",
  canceled: "Preklicano",
  refunded: "Povrnjeno",
};

const STATUS_TONE: Record<string, string> = {
  quote_requested: "bg-muted text-foreground/70",
  quote_sent: "bg-accent/10 text-accent",
  paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  shipped: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  delivered: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  canceled: "bg-destructive/10 text-destructive",
};

function Page() {
  const { groupId } = Route.useParams();
  const fetchGroup = useServerFn(getOrderGroup);
  const { data, isLoading } = useQuery({
    queryKey: ["order-group", groupId],
    queryFn: () => fetchGroup({ data: { group_id: groupId } }),
    refetchInterval: 15000,
  });

  if (isLoading) {
    return <div className="mx-auto max-w-4xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  }
  if (!data || data.orders.length === 0) {
    return <div className="mx-auto max-w-3xl px-6 py-24">Povpraševanje ne obstaja.</div>;
  }

  const orders = data.orders;
  const total = orders.reduce((s, o) => s + Number(o.amount_total), 0);
  const counts = {
    awaiting: orders.filter((o) => o.status === "quote_requested").length,
    sent: orders.filter((o) => o.status === "quote_sent").length,
    paid: orders.filter((o) => ["paid", "shipped", "delivered"].includes(o.status)).length,
    shipped: orders.filter((o) => ["shipped", "delivered"].includes(o.status)).length,
  };

  return (
    <section className="mx-auto max-w-4xl px-6 py-16">
      <Link to="/narocila" className="text-xs eyebrow hover:text-accent">← Vsa naročila</Link>
      <div className="eyebrow mt-3">Skupinsko povpraševanje</div>
      <h1 className="font-serif text-4xl md:text-5xl mt-2">Vaše povpraševanje pri {orders.length} prodajalcih</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Vsak prodajalec vam bo posebej poslal svoj predračun. Ko prejmete predračun, ga plačate po UPN
        nalogu na TRR prodajalca. Ko prodajalec potrdi plačilo, vam izda račun in odpošlje paket.
        Stanje vsakega dela naročila spremljate spodaj.
      </p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Skupno prodajalcev" value={orders.length} />
        <Stat label="Čaka na predračun" value={counts.awaiting} />
        <Stat label="Predračuni prejeti" value={counts.sent} />
        <Stat label="Plačano / odposlano" value={`${counts.paid}/${orders.length}`} />
      </div>

      <div className="mt-4 text-right text-sm">
        Skupni znesek povpraševanja: <span className="font-serif text-xl text-accent">€{total.toFixed(2)}</span>
        <div className="text-xs text-muted-foreground">brez poštnin — vsak prodajalec določi svojo</div>
      </div>

      <div className="mt-8 space-y-5">
        {orders.map((o) => {
          const vendorName = o.items[0]?.vendor_name ?? "Prodajalec";
          const tone = STATUS_TONE[o.status] ?? "bg-muted text-foreground/70";
          return (
            <div key={o.id} className="border border-border">
              <div className="flex items-center justify-between flex-wrap gap-3 p-5 bg-muted/30 border-b border-border">
                <div>
                  <div className="text-xs text-muted-foreground">
                    {vendorName}
                    {o.invoice_number && <> · Račun {o.invoice_number}</>}
                  </div>
                  <div className="font-serif text-lg">Del naročila #{o.id.slice(0, 8)}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xs uppercase tracking-wider px-2 py-1 ${tone}`}>
                    {STATUS_LABEL[o.status] ?? o.status}
                  </span>
                  <div className="font-serif text-lg text-accent">€{o.amount_total.toFixed(2)}</div>
                </div>
              </div>
              <div className="divide-y divide-border">
                {o.items.map((it) => (
                  <div key={it.id} className="p-4 flex items-center justify-between gap-4 text-sm">
                    <div className="min-w-0 flex-1 truncate">{it.title}</div>
                    <div className="text-muted-foreground">
                      {it.quantity}× <span className="text-accent">€{it.unit_price.toFixed(2)}</span>
                    </div>
                    <div className="font-serif text-accent w-20 text-right">€{it.line_total.toFixed(2)}</div>
                  </div>
                ))}
              </div>

              {(o.quote_id || o.quote_pdf_url || o.invoice_pdf_url || o.tracking_number) && (
                <div className="p-4 border-t border-border flex flex-wrap gap-4 text-xs items-center">
                  {o.quote_id && (
                    <Link to="/predracun/$id" params={{ id: o.quote_id }} className="underline underline-offset-4 hover:text-accent">
                      Pogled predračuna →
                    </Link>
                  )}
                  {o.quote_pdf_url && (
                    <a href={o.quote_pdf_url} target="_blank" rel="noopener" className="underline underline-offset-4 hover:text-accent">PDF predračun</a>
                  )}
                  {o.invoice_pdf_url && (
                    <a href={o.invoice_pdf_url} target="_blank" rel="noopener" className="underline underline-offset-4 hover:text-accent">PDF račun</a>
                  )}
                  {o.tracking_number && (
                    <span className="text-muted-foreground">
                      Sledenje: {o.tracking_carrier && <>{o.tracking_carrier} · </>}
                      <span className="font-mono">{o.tracking_number}</span>
                    </span>
                  )}
                </div>
              )}

              {o.status === "quote_requested" && (
                <div className="p-4 border-t border-border bg-muted/20 text-xs text-muted-foreground">
                  Prodajalec je prejel vaše povpraševanje in vam pripravlja predračun. Predračun bo viden tukaj
                  in poslan na vaš e-naslov.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="border border-border p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}
